import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { fetchReportBlobUrl } from '../../lib/forecasting_risk/report-client';
import type { ReportId } from '../../lib/forecasting_risk/report-client';
import { EMBEDDED_CONTENT_PROPS } from '../../lib/app/embedded-content';

interface ReportFrameProps {
  reportId: ReportId;
  title: string;
}

export default function ReportFrame({ reportId, title }: ReportFrameProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Incrementing this triggers a retry without duplicating the fetch logic.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);

    fetchReportBlobUrl(reportId)
      .then((url) => {
        objectUrl = url;
        setBlobUrl(url);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load report.');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reportId, attempt]);

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}
      >
        <Typography color="error">{error}</Typography>
        <Button variant="outlined" onClick={() => setAttempt((prev) => prev + 1)}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="iframe"
      src={blobUrl ?? undefined}
      sandbox="allow-scripts"
      title={title}
      {...EMBEDDED_CONTENT_PROPS}
      sx={{ flex: 1, width: '100%', border: 'none', display: 'block' }}
    />
  );
}
